import React, { createContext, useContext, useState, useEffect } from 'react';
import { STORAGE_KEYS } from '../constants/config';
import { userService } from '../services';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [currentUserFullDetails, setCurrentUserFullDetails] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initializeAuth = async () => {
      // Check for stored auth data on mount
      const storedToken = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
      const storedUser = localStorage.getItem(STORAGE_KEYS.USER_DATA);

      if (storedToken && storedUser) {
        setToken(storedToken);
        try {
          const parsedUser = JSON.parse(storedUser);
          setUser(parsedUser);

          // Fetch full profile details on reload
          try {
            const profileResponse = await userService.getProfile();
            const fullDetails = profileResponse.data || profileResponse;
            setCurrentUserFullDetails(fullDetails);
            // Sync localStorage user with latest profile if needed
            localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(fullDetails));
          } catch (profileError) {
            console.error('Error fetching full profile on init:', profileError);
          }
        } catch (error) {
          console.error('Error parsing user data:', error);
          // Clear invalid stored data
          localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
          localStorage.removeItem(STORAGE_KEYS.USER_DATA);
        }
      }
      setLoading(false);
    };

    initializeAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (authToken, userData, refreshToken) => {
    localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, authToken);
    localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(userData));
    if (refreshToken) {
      localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
    }
    setToken(authToken);
    setUser(userData);

    // Fetch full details immediately after login
    try {
      const profileResponse = await userService.getProfile();
      const fullDetails = profileResponse.data || profileResponse;
      setUser(fullDetails);
      setCurrentUserFullDetails(fullDetails);
      localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(fullDetails));
    } catch (error) {
      console.error('Error fetching full profile after login:', error);
    }
  };

  const logout = () => {
    localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.USER_DATA);
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    setToken(null);
    setUser(null);
    setCurrentUserFullDetails(null);
  };

  const updateUser = (userData) => {
    localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(userData));
    setUser(userData);
    setCurrentUserFullDetails(userData);
  };

  const refreshProfile = async () => {
    try {
      const profileResponse = await userService.getProfile();
      const fullDetails = profileResponse.data || profileResponse;
      setCurrentUserFullDetails(fullDetails);
      localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(fullDetails));
      setUser(fullDetails);
      return fullDetails;
    } catch (error) {
      console.error('Error refreshing profile:', error);
      throw error;
    }
  };

  const isAuthenticated = () => {
    return !!token && !!user;
  };

  const isAdmin = () => {
    return user && ['ADMIN', 'FINANCE_ADMIN', 'CONTENT_ADMIN', 'SUPPORT_ADMIN'].includes(user.role);
  };

  const isCandidate = () => {
    return user && user.role === 'CANDIDATE';
  };

  const isDonor = () => {
    return user && user.role === 'DONOR';
  };

  const value = {
    user,
    currentUserFullDetails,
    token,
    loading,
    login,
    logout,
    updateUser,
    refreshProfile,
    isAuthenticated,
    isAdmin,
    isCandidate,
    isDonor,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
